import { unzipSync } from "fflate";

export type RandomTeamsRosterMember = {
    /** Stable, roster-local key used by the existing team and drag/drop model. */
    id: number;
    name: string;
    /** A real student number from the source list; never synthesized. */
    studentNumber: number | null;
};

export type RandomTeamsRosterIssue = "duplicate-id" | "invalid-id" | "missing-name" | "no-names";

export type RandomTeamsRosterParseResult = {
    members: RandomTeamsRosterMember[];
    issues: RandomTeamsRosterIssue[];
};

type RosterCell = { value: string; quoted: boolean };

const MAX_STUDENT_NUMBER = 9999;
const MAX_IMPORT_FILE_BYTES = 8 * 1024 * 1024;

const addIssue = (issues: Set<RandomTeamsRosterIssue>, issue: RandomTeamsRosterIssue) => issues.add(issue);

const splitRosterText = (text: string): RosterCell[][] => {
    const rows: RosterCell[][] = [];
    let row: RosterCell[] = [];
    let value = "";
    let inQuotes = false;
    let fieldWasQuoted = false;

    const finishCell = () => {
        row.push({ value: value.trim(), quoted: fieldWasQuoted });
        value = "";
        fieldWasQuoted = false;
    };
    const finishRow = () => {
        finishCell();
        if (row.some((cell) => cell.value.length > 0)) rows.push(row);
        row = [];
    };

    for (let index = 0; index < text.length; index += 1) {
        const character = text[index] ?? "";

        if (character === '"') {
            if (inQuotes && text[index + 1] === '"') {
                value += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
                fieldWasQuoted = true;
            }
            continue;
        }

        if (!inQuotes && (character === "," || character === "\t")) {
            finishCell();
            continue;
        }

        if (!inQuotes && (character === "\n" || character === "\r")) {
            if (character === "\r" && text[index + 1] === "\n") index += 1;
            finishRow();
            continue;
        }

        value += character;
    }

    if (value.length > 0 || row.length > 0 || fieldWasQuoted) finishRow();
    return rows;
};

const normalizeHeader = (value: string): string => value.toLocaleLowerCase().replace(/[\s._#-]+/g, "").trim();

const isNameHeader = (value: string): boolean =>
    ["name", "names", "student", "studentname", "pupil", "pupilname", "이름", "학생", "학생이름"].includes(
        normalizeHeader(value)
    );

const isStudentNumberHeader = (value: string): boolean =>
    ["id", "studentid", "studentnumber", "number", "no", "학번", "번호", "학생번호", "학생id"].includes(
        normalizeHeader(value)
    );

const parseStudentNumber = (value: string): number | null => {
    if (!/^\d{1,4}$/.test(value)) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 && parsed <= MAX_STUDENT_NUMBER ? parsed : null;
};

const looksLikeStudentNumber = (value: string): boolean => /^\d+$/.test(value);

const cleanName = (value: string): string => value.trim().replace(/\s+/g, " ");

export const parseRandomTeamsRosterText = (input: string): RandomTeamsRosterParseResult => {
    const text = input.replace(/^\uFEFF/, "");
    const rows = splitRosterText(text);
    const issues = new Set<RandomTeamsRosterIssue>();
    const entries: Array<{ name: string; studentNumber: number | null }> = [];
    const seenNumbers = new Set<number>();

    const addEntry = (rawName: string, rawNumber: string | null = null) => {
        const name = cleanName(rawName);
        if (!name) {
            if (rawNumber) addIssue(issues, "missing-name");
            return;
        }

        let studentNumber: number | null = null;
        if (rawNumber) {
            studentNumber = parseStudentNumber(rawNumber);
            if (studentNumber === null) addIssue(issues, "invalid-id");
            else if (seenNumbers.has(studentNumber)) addIssue(issues, "duplicate-id");
            else seenNumbers.add(studentNumber);
        }

        entries.push({ name, studentNumber });
    };

    const firstRow = rows[0] ?? [];
    const nameColumn = firstRow.findIndex((cell) => isNameHeader(cell.value));
    const numberColumn = firstRow.findIndex((cell) => isStudentNumberHeader(cell.value));
    const hasHeader = nameColumn >= 0 && rows.length > 1;

    if (hasHeader) {
        rows.slice(1).forEach((row) => {
            const name = row[nameColumn]?.value ?? "";
            const number = numberColumn >= 0 ? row[numberColumn]?.value ?? "" : "";
            if (!name && !number) return;
            addEntry(name, number || null);
        });
    } else {
        rows.forEach((row) => {
            let index = 0;
            while (index < row.length) {
                const cell = row[index];
                if (!cell) break;
                const current = cell.value;
                if (!current) {
                    index += 1;
                    continue;
                }

                // A leading number followed by a name (in the next cell or after a space)
                // is the supported optional-ID form. Other commas and line breaks separate names.
                const prefixed = current.match(/^(\d{1,})[.)]?\s+(.+)$/);
                if (prefixed) {
                    addEntry(prefixed[2] ?? "", prefixed[1] ?? null);
                    index += 1;
                    continue;
                }

                const suffixed = current.match(/^(.+?)\s+(\d+)$/);
                if (suffixed) {
                    addEntry(suffixed[1] ?? "", suffixed[2] ?? null);
                    index += 1;
                    continue;
                }

                const next = row[index + 1]?.value ?? "";
                if (looksLikeStudentNumber(current) && next && !looksLikeStudentNumber(next)) {
                    addEntry(next, current);
                    index += 2;
                    continue;
                }

                if (!looksLikeStudentNumber(current) && looksLikeStudentNumber(next)) {
                    addEntry(current, next);
                    index += 2;
                    continue;
                }

                if (looksLikeStudentNumber(current)) {
                    addIssue(issues, "missing-name");
                    index += 1;
                    continue;
                }

                // Quoted CSV fields may contain commas and remain a single name.
                addEntry(current);
                index += 1;
            }
        });
    }

    if (entries.length === 0 && issues.size === 0) addIssue(issues, "no-names");

    return {
        members: entries.map((entry, index) => ({ id: index + 1, ...entry })),
        issues: [...issues]
    };
};

const decodeXml = (bytes: Uint8Array | undefined): string | null =>
    bytes ? new TextDecoder().decode(bytes) : null;

const parseXml = (text: string, description: string): Document => {
    const document = new DOMParser().parseFromString(text, "application/xml");
    if (document.getElementsByTagName("parsererror").length > 0) {
        throw new Error(`Invalid ${description}.`);
    }
    return document;
};

const elementsByLocalName = (document: Document, name: string): Element[] =>
    Array.from(document.getElementsByTagName("*")).filter((element) => element.localName === name);

const normalizeZipPath = (path: string): string => {
    const parts: string[] = [];
    path.replaceAll("\\", "/").split("/").forEach((part) => {
        if (!part || part === ".") return;
        if (part === "..") parts.pop();
        else parts.push(part);
    });
    return parts.join("/");
};

const columnIndexFromCellReference = (reference: string | null): number => {
    const letters = reference?.match(/^([A-Z]+)/i)?.[1]?.toUpperCase() ?? "A";
    let index = 0;
    for (const character of letters) index = index * 26 + character.charCodeAt(0) - 64;
    return Math.max(0, index - 1);
};

const readFirstWorksheet = (files: Record<string, Uint8Array>): string[][] => {
    const workbookXml = decodeXml(files["xl/workbook.xml"]);
    if (!workbookXml) throw new Error("The workbook has no worksheet list.");

    const workbook = parseXml(workbookXml, "workbook");
    const firstSheet = elementsByLocalName(workbook, "sheet")[0];
    if (!firstSheet) throw new Error("The workbook has no worksheets.");

    const relationshipId =
        firstSheet.getAttributeNS("http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id") ??
        firstSheet.getAttribute("r:id");
    const relationshipsXml = decodeXml(files["xl/_rels/workbook.xml.rels"]);
    let worksheetPath: string | null = null;

    if (relationshipId && relationshipsXml) {
        const relationships = parseXml(relationshipsXml, "workbook relationships");
        const relation = elementsByLocalName(relationships, "Relationship").find(
            (item) => item.getAttribute("Id") === relationshipId
        );
        const target = relation?.getAttribute("Target");
        if (target) worksheetPath = target.startsWith("/") ? normalizeZipPath(target) : normalizeZipPath(`xl/${target}`);
    }

    worksheetPath ??= Object.keys(files)
        .filter((path) => /^xl\/worksheets\/sheet\d+\.xml$/.test(path))
        .sort((left, right) => Number(left.match(/sheet(\d+)/)?.[1]) - Number(right.match(/sheet(\d+)/)?.[1]))[0] ?? null;

    const worksheetXml = worksheetPath ? decodeXml(files[worksheetPath]) : null;
    if (!worksheetXml) throw new Error("The first worksheet could not be read.");

    const sharedStringsXml = decodeXml(files["xl/sharedStrings.xml"]);
    const sharedStrings = sharedStringsXml
        ? elementsByLocalName(parseXml(sharedStringsXml, "shared strings"), "si").map((item) => item.textContent ?? "")
        : [];
    const worksheet = parseXml(worksheetXml, "worksheet");

    return elementsByLocalName(worksheet, "row").map((rowElement) => {
        const cells: string[] = [];
        const cellElements = Array.from(rowElement.getElementsByTagName("*")).filter((element) => element.localName === "c");
        cellElements.forEach((cell) => {
            const columnIndex = columnIndexFromCellReference(cell.getAttribute("r"));
            const type = cell.getAttribute("t");
            const valueElement = Array.from(cell.getElementsByTagName("*")).find((element) => element.localName === "v");
            const inlineText = Array.from(cell.getElementsByTagName("*")).find((element) => element.localName === "is");
            let value = valueElement?.textContent ?? "";
            if (type === "s") value = sharedStrings[Number(value)] ?? "";
            else if (type === "inlineStr") value = inlineText?.textContent ?? "";
            cells[columnIndex] = value;
        });
        return cells;
    });
};

const quoteTsvCell = (value: string): string =>
    /[,\t\r\n"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;

export const readRandomTeamsRosterFile = async (file: File): Promise<string> => {
    if (file.size > MAX_IMPORT_FILE_BYTES) throw new Error("File is too large.");
    const extension = file.name.toLocaleLowerCase().split(".").pop();
    if (extension === "txt" || extension === "csv" || extension === "tsv") return file.text();
    if (extension !== "xlsx") throw new Error("Unsupported file type.");

    if (typeof DOMParser !== "function") throw new Error("Excel import is unavailable in this browser.");
    const files = unzipSync(new Uint8Array(await file.arrayBuffer()));
    const rows = readFirstWorksheet(files);
    const text = rows.map((row) => row.map(quoteTsvCell).join("\t")).join("\n");
    if (!text.trim()) throw new Error("The first worksheet is empty.");
    return text;
};
